/**
 * WebCrypto helpers shared by both Workers. AES-256-GCM everywhere: it is
 * authenticated, so there is no separate MAC to get wrong, and it is available
 * in the Workers runtime and the browser without any polyfill or nodejs_compat.
 */

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export const IV_BYTES = 12;

// ---------------------------------------------------------------------------
// base64url
// ---------------------------------------------------------------------------

export function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  // Chunked so a large buffer cannot blow the argument limit of String.fromCharCode.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function utf8ToBase64Url(value: string): string {
  return toBase64Url(encoder.encode(value));
}

export function base64UrlToUtf8(value: string): string {
  return decoder.decode(fromBase64Url(value));
}

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

const keyCache = new Map<string, Promise<CryptoKey>>();

/**
 * Import a 32-byte secret (base64 or base64url) as an AES-GCM key.
 *
 * Keys are cached per isolate: importKey is not free and the stream Worker does
 * this on every single segment request.
 */
export function importAesKey(secret: string): Promise<CryptoKey> {
  const cached = keyCache.get(secret);
  if (cached) return cached;

  const promise = (async () => {
    const raw = fromBase64Url(secret.trim());
    if (raw.length !== 32) {
      throw new Error(`Expected a 32-byte base64 key, got ${raw.length} bytes`);
    }
    return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  })();

  keyCache.set(secret, promise);
  // A failed import must not poison the cache for the life of the isolate.
  promise.catch(() => keyCache.delete(secret));
  return promise;
}

/** Generate a fresh base64url key. Used by `pnpm gen:key`. */
export function generateKeyBase64Url(): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(32)));
}

// ---------------------------------------------------------------------------
// Seal / open
// ---------------------------------------------------------------------------

/** AES-GCM encrypt to `base64url( iv || ciphertext||tag )`. */
export async function seal(key: CryptoKey, plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, encoder.encode(plaintext)),
  );
  const out = new Uint8Array(iv.length + ciphertext.length);
  out.set(iv, 0);
  out.set(ciphertext, iv.length);
  return toBase64Url(out);
}

/** Inverse of {@link seal}. Returns null for anything tampered with or malformed. */
export async function open(key: CryptoKey, sealed: string): Promise<string | null> {
  try {
    const bytes = fromBase64Url(sealed);
    if (bytes.length <= IV_BYTES + 16) return null;
    const iv = bytes.subarray(0, IV_BYTES);
    const body = bytes.subarray(IV_BYTES);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, body as BufferSource);
    return decoder.decode(plaintext);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Hashing (session ids, magic-link tokens)
// ---------------------------------------------------------------------------

/** SHA-256, hex encoded. Used to store session/login tokens by hash. */
export async function sha256Hex(value: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', encoder.encode(value)));
  let hex = '';
  for (const byte of digest) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

/** URL-safe random id. 32 bytes by default (256 bits of entropy). */
export function randomId(bytes = 32): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

/**
 * Constant-time string comparison. Not strictly required where the value is
 * hashed first, but cheap insurance on the paths that compare raw secrets.
 */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/**
 * A short, public, non-reversible id for a shared secret.
 *
 * The API seals play tokens with `STREAM_TOKEN_KEY` and the stream Worker opens
 * them, so the two holding different values breaks every play URL — and nothing
 * observable from outside says so. Both Workers publish this id on their health
 * endpoints instead, which turns "do these two agree?" into comparing two
 * strings, at deploy time, before anyone clicks a channel.
 *
 * Publishing it discloses nothing usable: the input is 32 bytes of entropy
 * behind SHA-256, the output is truncated to 48 bits, and the domain prefix
 * stops the digest matching one taken of the same secret for another purpose.
 */
export async function keyFingerprint(secret: string): Promise<string> {
  const data = new TextEncoder().encode(`sflx-stream-token-key-id/${secret}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest).slice(0, 6)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * A static bearer secret both Workers can derive independently from the
 * `STREAM_TOKEN_KEY` they already share, so the stream Worker can authenticate
 * to the API Worker's error-reporting endpoint without a new secret to
 * provision. Unlike {@link keyFingerprint}, this is never published anywhere —
 * it doubles as the credential itself, not a public "do these two agree?" id.
 */
export async function streamErrorAuthToken(secret: string): Promise<string> {
  const data = new TextEncoder().encode(`sflx-stream-error-auth/${secret}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return toBase64Url(new Uint8Array(digest));
}
